import {
  Combobox,
  ComboboxContent,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  DatePicker,
  Input,
  LabelButton,
} from '@teamsnap/snap-ui';
import { debounce } from 'lodash';
import * as React from 'react';
import { useEffect } from 'react';
import { FinancialFilters } from './types';

type FinancialsTableFiltersProps = {
  filters: Partial<FinancialFilters>;
  setFilter: (
    key: keyof FinancialFilters,
    value: string | string[] | [Date, Date]
  ) => void;
  comboboxFilters: {
    key: keyof Omit<FinancialFilters, 'searchTerm' | 'datesRange' | 'dateType'>;
    label: string;
    options: { label: string; value: string }[] | null;
  }[];
  dateTypeOptions?: { label: string; value: string }[];
  searchTermPlaceholder?: string;
  onExport: () => void;
  onClearAll: () => void;
  onCaptureDateRangeAction?: (id: string, date?: [Date, Date]) => void;
};

const DEBOUNCE_TIME = 300;
const today = new Date().getDate();
export const datesRange: [Date, Date] = [
  new Date(new Date().setDate(today - 30)),
  new Date(new Date().setDate(today + 30)),
];

export default function FinancialsTableFilters(
  props: FinancialsTableFiltersProps
) {
  const {
    filters,
    setFilter,
    comboboxFilters,
    onExport,
    onClearAll,
    dateTypeOptions,
    onCaptureDateRangeAction,
  } = props;

  const [localSearchTerm, setLocalSearchTerm] = React.useState('');
  const [showMoreFilters, setShowMoreFilters] = React.useState(false);

  useEffect(() => {
    if (filters.searchTerm && !localSearchTerm) {
      setLocalSearchTerm(filters.searchTerm);
    }
  }, [filters.searchTerm]);

  const hasActiveComboboxFilters = React.useMemo(
    () =>
      comboboxFilters.some(
        (comboboxFilter) => filters[comboboxFilter.key]?.length > 0
      ),
    [comboboxFilters, filters]
  );

  const handleOnClearAllComboboxes = () => onClearAll();

  React.useEffect(() => {
    if (hasActiveComboboxFilters) {
      setShowMoreFilters(true);
    }
  }, [hasActiveComboboxFilters]);

  // Debouncing so that we don't set the filter on every keystroke and slow the page down
  // (unless the user is a very slow typer, then it's on them)
  const debouncedSearchTerm = React.useCallback(
    debounce((value) => {
      setFilter('searchTerm', value);
    }, DEBOUNCE_TIME),
    []
  );

  const handleSearchTermChange = (e) => {
    const { value } = e.target;
    setLocalSearchTerm(value);
    debouncedSearchTerm(value);
  };

  return (
    <div className="sui-mb-2">
      <div className="sui-flex sui-gap-1 sui-mb-0.5 sui-justify-end">
        {filters.searchTerm !== undefined && (
          <Input
            name="search"
            leftIcon="search"
            allowClear
            type="text"
            placeholder={props.searchTermPlaceholder || 'Search by member name, order number or source'}
            size="small"
            className="sui-flex-1"
            value={localSearchTerm}
            onChange={handleSearchTermChange}
          />
        )}

        {dateTypeOptions && dateTypeOptions.length > 0 && (
          <Input
            name="dateType"
            onChange={(e) => setFilter('dateType', e.target.value)}
            options={dateTypeOptions}
            value={filters.dateType}
            type="select"
            size="small"
            className="sui-max-w-[150px] [&>div>select]:sui-bg-white"
          />
        )}
        <DatePicker
          value={filters.datesRange}
          onChange={(date) => setFilter('datesRange', date)}
          size="small"
          placeholder="Date Range"
          range
          fixedDatesShortcut
          calendarAlign="end"
          data-testid="date-range-picker"
          onCaptureAction={(_, id, date) => onCaptureDateRangeAction(id, date)}
        />
        <LabelButton
          size="small"
          variantType="secondary"
          iconPosition="left"
          onClick={() => setShowMoreFilters(!showMoreFilters)}
          icon="filter_alt"
          data-testid="FinancialsTableFilters--more-filters-button"
          labelText="More Filters"
        />
        <LabelButton
          size="small"
          variantType="secondary"
          onClick={onExport}
          data-testid="FinancialsTableFilters--export-button"
          labelText="Export ..."
        />
      </div>
      {showMoreFilters && (
        <div className="sui-py-1 sui-px-2 sui-flex sui-gap-1 sui-bg-neutral-background-weak sui-rounded sui-flex-wrap">
          {comboboxFilters.map(
            (comboboxFilter) =>
              comboboxFilter.options &&
              comboboxFilter.options.length > 0 && (
                <Combobox
                  values={filters[comboboxFilter.key]}
                  onValuesChange={(values) =>
                    setFilter(comboboxFilter.key, values)
                  }
                  key={`combobox-filter-${comboboxFilter.key}`}
                >
                  <ComboboxTrigger
                    placeholder={comboboxFilter.label}
                    data-testid={`FinancialsTableFilters--${comboboxFilter.key}-filters`}
                  />
                  <ComboboxContent
                    className="sui-max-w-[320px]"
                    data-testid={`FinancialsTableFilters--${comboboxFilter.key}-list`}
                  >
                    <ComboboxList>
                      {comboboxFilter.options.map((option) => (
                        <ComboboxItem
                          key={`combobox-option-${comboboxFilter.key}-${option.label}-${option.value}`}
                          value={option.value}
                          label={option.label}
                        />
                      ))}
                    </ComboboxList>
                  </ComboboxContent>
                </Combobox>
              )
          )}

          {hasActiveComboboxFilters && (
            <LabelButton
              size="small"
              variantType="tertiary"
              data-testid="FinancialsTableFilters--clear-all-button"
              onClick={handleOnClearAllComboboxes}
            >
              Clear All
            </LabelButton>
          )}
        </div>
      )}
    </div>
  );
}
